<template>
  <el-dialog
    :visible="active"
    width="1120px"
    top="4vh"
    append-to-body
    :show-close="false"
    :close-on-click-modal="false"
    custom-class="assign-hw"
    @update:visible="(val) => $emit('update:active', val)"
  >
    <div slot="title" class="assign-hw__head">
      <div>
        <h3>{{ isMulti ? 'Giao nhiều bài tập cùng lúc' : 'Giao bài tập' }}</h3>
        <p>{{ examList.length }} bài tập từ {{ categoryGroups.length }} danh mục · {{ kindCount }} loại bài</p>
      </div>
      <button type="button" class="assign-hw__close" @click="closeDialog">
        <i class="el-icon-close"></i>
      </button>
    </div>

    <div class="assign-hw__body">
      <div class="assign-hw__main">
        <!-- 1. Lớp -->
        <section class="assign-hw__section">
          <h4><span class="assign-hw__no">{{ sectionNo('classes') }}</span> Giao cho lớp</h4>
          <div class="assign-hw__classes">
            <div
              v-for="cId in selectedClassIds"
              :key="cId"
              :class="['assign-hw__class', { 'is-open': openClassId === cId }]"
            >
              <el-checkbox :value="true" @change="removeClass(cId)" />
              <span class="assign-hw__class-text" @click="toggleStudents(cId)">
                <strong>{{ getClassName(cId) }}</strong>
                <small>{{ classStudentText(cId) }}</small>
              </span>
            </div>

            <el-popover v-model="classPickerOpen" placement="bottom-start" width="300" trigger="click" popper-class="assign-hw-picker">
              <el-input v-model="classSearch" size="small" prefix-icon="el-icon-search" placeholder="Tìm lớp" clearable />
              <div class="assign-hw-picker__list">
                <el-checkbox
                  v-for="cls in filteredClasses"
                  :key="cls._id"
                  :value="selectedClassIds.includes(cls._id)"
                  @change="(val) => (val ? addClass(cls._id) : removeClass(cls._id))"
                >{{ cls.name }}</el-checkbox>
                <span v-if="!filteredClasses.length" class="assign-hw__muted">Không có lớp phù hợp</span>
              </div>
              <button slot="reference" type="button" class="assign-hw__add-class">
                <i class="el-icon-plus"></i> Thêm lớp
              </button>
            </el-popover>
          </div>

          <div v-if="openClassId && selectedClassIds.includes(openClassId)" class="assign-hw__students">
            <div class="assign-hw__students-head">
              <span>Học sinh lớp <strong>{{ getClassName(openClassId) }}</strong></span>
              <a @click="selectAllStudents(openClassId)">{{ isAllStudentsSelected(openClassId) ? 'Bỏ chọn tất cả' : 'Chọn cả lớp' }}</a>
            </div>
            <button
              v-for="student in studentsByClass[openClassId] || []"
              :key="student.eID"
              type="button"
              :class="['assign-hw__student', { 'is-active': (assignsMap[openClassId] || []).includes(student.eID) }]"
              @click="handleStudentCheckboxChange(openClassId, student.eID, !(assignsMap[openClassId] || []).includes(student.eID))"
            >
              <img v-if="student.avatar" :src="student.avatar | media" />
              <span v-else class="assign-hw__avatar">{{ (student.displayName || student.fullname || '?').charAt(0) }}</span>
              {{ student.displayName || student.fullname }}
            </button>
            <span v-if="!(studentsByClass[openClassId] || []).length" class="assign-hw__muted">Lớp chưa có học sinh</span>
          </div>
          <p v-else-if="selectedClassIds.length" class="assign-hw__hint">Bấm vào tên lớp để chọn riêng học sinh.</p>
        </section>

        <!-- 2. Hình thức -->
        <section v-if="isMulti" class="assign-hw__section">
          <h4><span class="assign-hw__no">{{ sectionNo('mode') }}</span> Hình thức giao</h4>
          <div class="assign-hw__modes">
            <button type="button" :class="['assign-hw__mode', { 'is-active': mode === 'separate' }]" @click="mode = 'separate'">
              <i class="el-icon-document-copy"></i>
              <span>
                <strong>Giao từng bài</strong>
                <small>Mỗi bài là một bài tập riêng</small>
              </span>
            </button>
            <el-tooltip :disabled="!mergeIssue" :content="mergeIssue || ''" placement="top">
              <button
                type="button"
                :class="['assign-hw__mode', { 'is-active': mode === 'merge', 'is-disabled': !!mergeIssue }]"
                @click="!mergeIssue && (mode = 'merge')"
              >
                <i class="el-icon-files"></i>
                <span>
                  <strong>Gộp thành 1 bài tập</strong>
                  <small>{{ mergeIssue || 'Học sinh làm tuần tự trong 1 bài tập' }}</small>
                </span>
              </button>
            </el-tooltip>
          </div>
        </section>

        <!-- 3. Tên -->
        <section class="assign-hw__section">
          <h4><span class="assign-hw__no">{{ sectionNo('name') }}</span> Tên bài giao</h4>

          <template v-if="mode === 'separate'">
            <template v-if="isMulti">
              <label class="assign-hw__label">Tiền tố chung (tuỳ chọn) · áp cho cả {{ examList.length }} bài</label>
              <el-input ref="prefixInput" v-model="prefix" placeholder="Ví dụ: Bài tập ngày {ngày giao} – " class="assign-hw__prefix">
                <span slot="suffix" class="assign-hw__suffix">+ tên bài gốc</span>
              </el-input>
              <div class="assign-hw__tokens">
                Chèn nhanh:
                <button v-for="token in NAME_TOKENS" :key="token" type="button" @click="insertToken('prefix', token)">{{ token }}</button>
              </div>
              <p class="assign-hw__hint">Tên hiển thị = tiền tố + tên bài gốc. Sửa trực tiếp từng bài bên dưới; bài trùng tên sẽ tự thêm loại bài.</p>
            </template>

            <div class="assign-hw__names">
              <div class="assign-hw__names-head">
                <span>Bài gốc</span>
                <span>Tên hiển thị cho học sinh</span>
                <a v-if="hasOverrides" @click="overrides = {}">Đặt lại tất cả</a>
              </div>
              <div v-for="row in nameRows" :key="row.item._id" class="assign-hw__name-row">
                <span class="assign-hw__origin">
                  <span class="assign-hw__tag" :style="{ background: itemTag(row.item).bg, color: itemTag(row.item).color }">{{ itemTag(row.item).label }}</span>
                  <span class="assign-hw__origin-title" :title="row.item.title">{{ row.item.title }}</span>
                </span>
                <i class="el-icon-right assign-hw__arrow"></i>
                <el-input
                  :value="row.name"
                  size="small"
                  :class="{ 'is-edited': row.edited }"
                  @input="(val) => $set(overrides, row.item._id, val)"
                />
                <span class="assign-hw__flag">
                  <span v-if="row.edited" class="assign-hw__flag--edited">Đã sửa</span>
                  <span v-else-if="row.kindAdded" class="assign-hw__flag--kind">Tự thêm loại</span>
                </span>
                <el-tooltip content="Xem trước" placement="top">
                  <el-button type="text" icon="el-icon-view" class="assign-hw__preview" @click="itemPreview = { visible: true, examId: row.item._id }" />
                </el-tooltip>
              </div>
            </div>
          </template>

          <template v-else>
            <el-input ref="mergeNameInput" v-model="mergeName" placeholder="Tên bài tập gộp" />
            <div class="assign-hw__tokens">
              Chèn nhanh:
              <button v-for="token in NAME_TOKENS" :key="token" type="button" @click="insertToken('mergeName', token)">{{ token }}</button>
            </div>
            <div class="assign-hw__items-head">
              <span>Đề trong bài gộp <small>(kéo để sắp xếp thứ tự câu hỏi)</small></span>
              <el-button size="mini" plain icon="el-icon-view" @click="previewVisible = true">Xem trước</el-button>
            </div>
            <draggable v-model="mergeOrder" class="assign-hw__items" handle=".assign-hw__drag" ghost-class="is-ghost">
              <div v-for="(item, i) in mergeOrder" :key="item._id" class="assign-hw__item">
                <i class="el-icon-rank assign-hw__drag"></i>
                <span class="assign-hw__item-index">{{ i + 1 }}</span>
                <span class="assign-hw__tag" :style="{ background: itemTag(item).bg, color: itemTag(item).color }">{{ itemTag(item).label }}</span>
                <span class="assign-hw__origin-title">{{ item.title }}</span>
              </div>
            </draggable>
          </template>
        </section>

        <!-- 4. Thời gian -->
        <section class="assign-hw__section">
          <h4><span class="assign-hw__no">{{ sectionNo('time') }}</span> Thời gian giao</h4>
          <div class="assign-hw__time">
            <div class="assign-hw__row">
              <div>
                <label class="assign-hw__label">Mở giao</label>
                <el-date-picker v-model="hwTemp.startAt" type="datetime" format="dd/MM/yyyy · HH:mm" :clearable="false" class="assign-hw__full" />
              </div>
              <div>
                <label class="assign-hw__label">Hạn nộp</label>
                <el-date-picker v-model="hwTemp.endAt" type="datetime" format="dd/MM/yyyy · HH:mm" :clearable="false" class="assign-hw__full" />
              </div>
            </div>
          </div>
        </section>

        <!-- 5. Tuỳ chọn -->
        <section class="assign-hw__section">
          <h4><span class="assign-hw__no">{{ sectionNo('options') }}</span> Tuỳ chọn</h4>
          <div class="assign-hw__row">
            <div v-if="config && config.subjects">
              <label class="assign-hw__label">Môn học <em>*</em></label>
              <el-select v-model="hwTemp.subject" class="assign-hw__full" placeholder="Chọn môn học" filterable>
                <el-option v-for="opt in config.subjects" :key="opt._id" :label="opt[`${$i18n.locale}Value`]" :value="opt._id" />
              </el-select>
            </div>
            <div>
              <label class="assign-hw__label">Số lần nộp</label>
              <div class="assign-hw__limit">
                <el-checkbox v-model="unlimitedSubmit">Không giới hạn</el-checkbox>
                <el-input-number v-if="!unlimitedSubmit" v-model="hwTemp.limitSubmit" :min="1" :max="100" size="small" controls-position="right" />
              </div>
            </div>
          </div>
          <div class="assign-hw__toggle">
            <span>Xuất bản ngay <small>{{ hwTemp.publish ? 'Học sinh thấy bài từ thời gian mở giao' : 'Lưu nháp, xuất bản sau' }}</small></span>
            <el-switch v-model="hwTemp.publish" active-color="#1cab36" />
          </div>
          <div class="assign-hw__toggle">
            <span>{{ $t('SHOW_ANSWER_KEY') }} <small>Cho học sinh xem đáp án sau khi nộp</small></span>
            <el-switch v-model="hwTemp.showAnswerKey" active-color="#1cab36" />
          </div>
        </section>
      </div>

      <!-- Tóm tắt -->
      <aside class="assign-hw__summary">
        <h4>Tóm tắt giao bài</h4>
        <div class="assign-hw__stats">
          <div><strong>{{ examList.length }}</strong><small>bài tập</small></div>
          <div><strong>{{ selectedClassIds.length }}</strong><small>lớp</small></div>
          <div><strong>{{ homeworkCount }}</strong><small>lượt giao</small></div>
        </div>
        <div v-for="group in categoryGroups" :key="group.key" class="assign-hw__group">
          <div class="assign-hw__group-head">
            <span :title="group.label">{{ group.label }}</span>
            <small>{{ group.items.length }} bài</small>
          </div>
          <div class="assign-hw__group-tags">
            <span
              v-for="kind in group.kinds"
              :key="kind"
              class="assign-hw__tag"
              :style="{ background: KIND_META[kind].bg, color: KIND_META[kind].color }"
            >{{ KIND_META[kind].label }}</span>
          </div>
        </div>
        <dl class="assign-hw__facts">
          <div><dt>Hình thức</dt><dd>{{ mode === 'merge' ? 'Gộp thành 1 bài tập' : isMulti ? 'Giao từng bài' : 'Giao 1 bài' }}</dd></div>
          <div><dt>Tên bài giao</dt><dd>{{ nameSummary }}</dd></div>
          <div><dt>Mở giao</dt><dd>{{ formatDateTime(hwTemp.startAt) }}</dd></div>
          <div><dt>Hạn nộp</dt><dd>{{ formatDateTime(hwTemp.endAt) }}</dd></div>
          <div><dt>Số lần nộp</dt><dd>{{ unlimitedSubmit ? 'Không giới hạn' : hwTemp.limitSubmit }}</dd></div>
          <div><dt>Trạng thái</dt><dd>{{ hwTemp.publish ? 'Xuất bản ngay' : 'Nháp' }}</dd></div>
        </dl>
      </aside>
    </div>

    <div slot="footer" class="assign-hw__foot">
      <span class="assign-hw__foot-text">
        Giao {{ examList.length }} bài tập cho {{ selectedClassIds.length }} lớp · {{ assignedStudentCount }} học sinh
      </span>
      <el-button @click="closeDialog">Huỷ</el-button>
      <el-button type="primary" icon="el-icon-s-promotion" :loading="isSubmitting" :disabled="!!submitIssue" @click="submit">
        {{ submitLabel }}
      </el-button>
    </div>

    <MergedPreviewDialog :visible.sync="previewVisible" :items="mergeOrder" />
    <ExamPreviewDialog :visible.sync="itemPreview.visible" :examId="itemPreview.examId" />
  </el-dialog>
</template>

<script>
import { mapState } from 'vuex'
import draggable from 'vuedraggable'
import { getStudents } from '@/api/content'
import examVaultApi from '@/api/exam-vault'
import { KIND_META, cartIssue, lastCategoryOf } from '../library/kinds'
import { NAME_TOKENS, buildItemNames, resolveTokens, formatDateTime } from '../library/assign-names'
import MergedPreviewDialog from './MergedPreviewDialog.vue'
import ExamPreviewDialog from './ExamPreviewDialog.vue'

const SECTION_ORDER = ['classes', 'mode', 'name', 'time', 'options']

export default {
  name: 'AssignHomeworkDialog',
  components: { draggable, MergedPreviewDialog, ExamPreviewDialog },

  props: {
    active: { type: Boolean, default: false },
    exam: { type: Object, default: () => null },
    exams: { type: Array, default: null }
  },

  data() {
    return {
      KIND_META,
      NAME_TOKENS,
      isSubmitting: false,
      previewVisible: false,
      itemPreview: { visible: false, examId: null },
      mode: 'separate',
      prefix: '',
      overrides: {},
      mergeName: '',
      mergeOrder: [],
      hwTemp: {
        subject: null,
        startAt: '',
        endAt: '',
        limitSubmit: -1,
        publish: false,
        showAnswerKey: false
      },
      selectedClassIds: [],
      classPickerOpen: false,
      classSearch: '',
      openClassId: null,
      studentsByClass: {}, // classId -> [students]
      assignsMap: {} // classId -> [eID1, eID2, ...]
    }
  },

  computed: {
    ...mapState('content', ['classes']),
    ...mapState('exam-vault', ['config']),

    examList() {
      if (this.exams && this.exams.length) return this.exams
      return this.exam ? [this.exam] : []
    },
    isMulti() {
      return this.examList.length > 1
    },
    mergeIssue() {
      return cartIssue(this.examList)
    },
    kindCount() {
      return new Set(this.examList.map((item) => item.kind)).size
    },
    categoryGroups() {
      const map = new Map()
      for (const item of this.examList) {
        const last = lastCategoryOf(item)
        const key = last ? last._id : 'none'
        if (!map.has(key)) {
          const label = (item.categoryPath || []).map((p) => p.name).join(' › ') || 'Không có danh mục'
          map.set(key, { key, label, items: [], kinds: [] })
        }
        const group = map.get(key)
        group.items.push(item)
        if (KIND_META[item.kind] && !group.kinds.includes(item.kind)) group.kinds.push(item.kind)
      }
      return [...map.values()]
    },
    nameRows() {
      return buildItemNames(this.examList, {
        prefix: this.isMulti ? this.prefix : '',
        overrides: this.overrides,
        startAt: this.hwTemp.startAt
      })
    },
    hasOverrides() {
      return Object.keys(this.overrides).length > 0
    },
    resolvedMergeName() {
      return resolveTokens(this.mergeName, { startAt: this.hwTemp.startAt }).trim()
    },
    nameSummary() {
      if (this.mode === 'merge') return this.resolvedMergeName || '—'
      if (!this.isMulti) return this.nameRows[0] ? this.nameRows[0].name : '—'
      return this.prefix.trim() ? 'Tiền tố + tên bài gốc' : 'Tên bài gốc'
    },
    filteredClasses() {
      const search = this.classSearch.trim().toLowerCase()
      return (this.classes || []).filter((cls) => !search || (cls.name || '').toLowerCase().includes(search))
    },
    homeworkCount() {
      return (this.mode === 'merge' ? 1 : this.examList.length) * this.selectedClassIds.length
    },
    assignedStudentCount() {
      return this.selectedClassIds.reduce((sum, cId) => sum + (this.assignsMap[cId] || []).length, 0)
    },
    // limitSubmit = -1 là không giới hạn
    unlimitedSubmit: {
      get() {
        return !(Number(this.hwTemp.limitSubmit) > 0)
      },
      set(value) {
        this.hwTemp.limitSubmit = value ? -1 : 1
      }
    },
    submitIssue() {
      if (this.isSubmitting) return 'submitting'
      if (!this.selectedClassIds.length) return 'Chọn ít nhất 1 lớp'
      if (this.mode === 'merge') return this.mergeIssue || (this.resolvedMergeName ? null : 'Nhập tên bài giao')
      return this.nameRows.some((row) => !row.name) ? 'Nhập tên cho tất cả bài' : null
    },
    submitLabel() {
      const classes = this.selectedClassIds.length
      if (this.mode === 'merge') return `Giao 1 bài gộp cho ${classes} lớp`
      return `Giao ${this.examList.length} bài cho ${classes} lớp`
    }
  },

  watch: {
    active(val) {
      if (val && this.examList.length) this.resetForm()
    }
  },

  methods: {
    formatDateTime,

    sectionNo(key) {
      const keys = SECTION_ORDER.filter((k) => k !== 'mode' || this.isMulti)
      return keys.indexOf(key) + 1
    },

    kindMeta(item) {
      return KIND_META[item.kind] || KIND_META.exercise
    },

    // Bài có kỹ năng hiện tag kỹ năng (cùng màu lib-tag--skill ở danh sách), không thì tag loại bài
    itemTag(item) {
      if (item.skill) return { label: item.skill[`${this.$i18n.locale}Value`] || item.skill.viValue, bg: '#eef2ff', color: '#4f46e5' }
      return this.kindMeta(item)
    },

    resetForm() {
      const first = this.examList[0]
      this.mode = 'separate'
      this.prefix = ''
      this.overrides = {}
      this.mergeOrder = [...this.examList]
      this.mergeName = this.isMulti ? `${first.title} (+${this.examList.length - 1})` : first.title || ''
      this.hwTemp = {
        subject: first.subject?._id || first.subject,
        startAt: new Date(),
        endAt: new Date(new Date().getTime() + 7 * 24 * 60 * 60 * 1000), // +7 days
        limitSubmit: -1,
        publish: false,
        showAnswerKey: false
      }
      this.selectedClassIds = []
      this.classSearch = ''
      this.openClassId = null
      this.studentsByClass = {}
      this.assignsMap = {}
    },

    closeDialog() {
      this.$emit('update:active', false)
    },

    insertToken(field, token) {
      this[field] = `${this[field] || ''}${token}`
      const ref = field === 'prefix' ? this.$refs.prefixInput : this.$refs.mergeNameInput
      if (ref) this.$nextTick(() => ref.focus())
    },

    getClassName(classId) {
      const cls = (this.classes || []).find((c) => c._id === classId)
      return cls ? cls.name : 'Lớp không xác định'
    },

    classStudentText(classId) {
      const total = (this.studentsByClass[classId] || []).length
      const picked = (this.assignsMap[classId] || []).length
      if (!this.studentsByClass[classId]) return 'Đang tải...'
      return picked === total ? `${total} học sinh` : `${picked}/${total} học sinh`
    },

    async addClass(classId) {
      if (this.selectedClassIds.includes(classId)) return
      this.selectedClassIds = this.selectedClassIds.concat(classId)
      if (this.studentsByClass[classId]) return
      try {
        const res = await getStudents({ classId })
        const students = ((res && res.data && res.data.students) || []).filter((d) => d.eID && d.status === 'approved')
        this.$set(this.studentsByClass, classId, students)
        // Mặc định giao cả lớp
        this.$set(this.assignsMap, classId, students.map((s) => s.eID))
      } catch (error) {
        console.error('Failed to fetch students for class', classId, error)
        this.$set(this.studentsByClass, classId, [])
        this.$set(this.assignsMap, classId, [])
      }
    },

    removeClass(classId) {
      this.selectedClassIds = this.selectedClassIds.filter((id) => id !== classId)
      this.$delete(this.assignsMap, classId)
      this.$delete(this.studentsByClass, classId)
      if (this.openClassId === classId) this.openClassId = null
    },

    toggleStudents(classId) {
      this.openClassId = this.openClassId === classId ? null : classId
    },

    handleStudentCheckboxChange(classId, eID, checked) {
      const current = this.assignsMap[classId] || []
      if (checked) {
        if (!current.includes(eID)) this.$set(this.assignsMap, classId, [...current, eID])
      } else {
        this.$set(this.assignsMap, classId, current.filter((id) => id !== eID))
      }
    },

    selectAllStudents(classId) {
      const students = this.studentsByClass[classId] || []
      this.$set(this.assignsMap, classId, this.isAllStudentsSelected(classId) ? [] : students.map((s) => s.eID))
    },

    isAllStudentsSelected(classId) {
      const current = this.assignsMap[classId] || []
      const students = this.studentsByClass[classId] || []
      return students.length > 0 && current.length === students.length
    },

    getMappedSubject(subjectId) {
      if (!subjectId) return null

      const subjectMap = {
        english: 'en',
        math: 'math',
        vietnamese: 'vi',
        chemistry: 'chem',
        chinese: 'zh',
        vi: 'vi',
        en: 'en'
      }

      if (this.config && this.config.subjects) {
        const found = this.config.subjects.find((s) => s._id === subjectId)
        if (found && found.value) {
          const valLower = found.value.toLowerCase()
          return subjectMap[valLower] || valLower.substring(0, 2)
        }
      }

      const idLower = subjectId.toLowerCase()
      return subjectMap[idLower] || idLower.substring(0, 2)
    },

    async submit() {
      if (this.submitIssue) return

      const payload = {
        ...this.hwTemp,
        subject: this.getMappedSubject(this.hwTemp.subject),
        assigns: this.selectedClassIds.map((classId) => ({
          classId,
          assignedEIDs: this.assignsMap[classId] || []
        }))
      }
      if (this.mode === 'merge') {
        // Thứ tự câu hỏi = thứ tự đề trong danh sách
        payload.examVaultIds = this.mergeOrder.map((item) => item._id)
        payload.name = this.resolvedMergeName
      } else {
        payload.items = this.nameRows.map((row) => ({ examVaultId: row.item._id, name: row.name }))
      }

      this.isSubmitting = true
      try {
        await examVaultApi.createMergedHomework(payload)
        this.$emit('success')
        this.closeDialog()
      } catch (error) {
        // request.js đã hiện thông báo lỗi
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style lang="scss">
// el-dialog / popover không nằm trong scope của component -> style khung để global
.el-dialog.assign-hw {
  max-width: calc(100vw - 32px);
  border-radius: 16px;
  overflow: hidden;

  .el-dialog__header,
  .el-dialog__body,
  .el-dialog__footer {
    padding: 0;
  }
}

.assign-hw-picker__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 260px;
  margin-top: 10px;
  overflow: auto;
}
</style>

<style scoped lang="scss">
$green: #1cab36;

.assign-hw {
  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 22px 28px 16px;
    border-bottom: 2px solid #eef0ef;

    h3 {
      margin: 0;
      color: #111827;
      font-size: 22px;
    }

    p {
      margin: 4px 0 0;
      color: #6b7280;
      font-size: 13px;
    }
  }

  &__close {
    width: 36px;
    height: 36px;
    border: 0;
    border-radius: 50%;
    background: #f2f4f3;
    font-size: 16px;
    cursor: pointer;
  }

  &__body {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 360px;
  }

  // max-height on the grid itself doesn't cap the row track, so content would overflow onto the footer
  &__main,
  &__summary {
    max-height: calc(100vh - 200px);
  }

  &__main {
    padding: 8px 28px 24px;
    overflow: auto;
  }

  &__section {
    padding-top: 20px;

    h4 {
      display: flex;
      align-items: center;
      gap: 10px;
      margin: 0 0 12px;
      color: #111827;
      font-size: 16px;
    }
  }

  &__no {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #e8f6eb;
    color: $green;
    font-size: 12px;
    font-weight: 700;
  }

  &__classes {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  &__class {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 130px;
    padding: 8px 14px 8px 12px;
    border: 1.5px solid $green;
    border-radius: 10px;
    background: #f1faf3;

    &.is-open {
      box-shadow: 0 0 0 3px rgba(28, 171, 54, 0.15);
    }
  }

  &__class-text {
    display: flex;
    flex-direction: column;
    cursor: pointer;

    strong {
      color: $green;
      font-size: 14px;
    }

    small {
      color: #6b7280;
      font-size: 12px;
    }
  }

  &__add-class {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 52px;
    padding: 0 18px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
    color: $green;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }

  &__students {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;
    padding: 12px;
    border: 1px solid #eef0ef;
    border-radius: 10px;
    background: #fafbfa;
  }

  &__students-head {
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 4px;
    font-size: 13px;

    a {
      color: $green;
      cursor: pointer;
    }
  }

  &__student {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px 3px 3px;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    background: #fff;
    color: #6b7280;
    font-size: 12px;
    cursor: pointer;

    img,
    .assign-hw__avatar {
      width: 22px;
      height: 22px;
      border-radius: 50%;
      object-fit: cover;
    }

    &.is-active {
      border-color: $green;
      background: #e8f6eb;
      color: #14532d;
    }
  }

  &__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #e5e7eb;
    color: #4b5563;
    font-weight: 600;
  }

  &__modes {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  &__mode {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
    padding: 14px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
    text-align: left;
    cursor: pointer;

    i {
      margin-top: 2px;
      color: #6b7280;
      font-size: 18px;
    }

    span {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    strong {
      color: #111827;
      font-size: 14px;
    }

    small {
      color: #6b7280;
      font-size: 12px;
    }

    &.is-active {
      border: 1.5px solid $green;
      background: #f1faf3;

      i,
      strong {
        color: $green;
      }
    }

    &.is-disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }

  &__label {
    display: block;
    margin: 0 0 6px;
    color: #4b5563;
    font-size: 13px;

    em {
      color: #e0484d;
      font-style: normal;
    }
  }

  &__prefix ::v-deep .el-input__inner {
    border-color: $green;
  }

  &__suffix {
    margin-right: 6px;
    color: #9ca3af;
    font-size: 12px;
    line-height: 40px;
  }

  &__tokens {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    color: #6b7280;
    font-size: 12px;

    button {
      padding: 3px 10px;
      border: 0;
      border-radius: 6px;
      background: #e8f6eb;
      color: $green;
      font-size: 12px;
      cursor: pointer;
    }
  }

  &__hint {
    margin: 10px 0 0;
    color: #6b7280;
    font-size: 12px;
  }

  &__names {
    margin-top: 12px;
    border: 1px solid #e8ebe9;
    border-radius: 10px;
    overflow: hidden;
  }

  &__names-head,
  &__name-row {
    display: grid;
    grid-template-columns: 190px 18px minmax(0, 1fr) 84px 24px;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
  }

  &__names-head {
    background: #f7f8f7;
    color: #4b5563;
    font-size: 12px;

    span:nth-child(2) {
      grid-column: 2 / 4;
    }

    a {
      color: $green;
      font-weight: 600;
      text-align: right;
      cursor: pointer;
    }
  }

  &__name-row {
    border-top: 1px solid #f0f1f0;

    .assign-hw__preview {
      padding: 0;
      color: #6b7280;
      font-size: 16px;

      &:hover {
        color: $green;
      }
    }

    .is-edited ::v-deep .el-input__inner {
      border-color: $green;
      background: #f1faf3;
    }
  }

  &__origin {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  &__origin-title {
    overflow: hidden;
    color: #6b7280;
    font-size: 13px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__arrow {
    color: #c4c9c6;
  }

  &__tag {
    flex-shrink: 0;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
    white-space: nowrap;
  }

  &__flag span {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
    white-space: nowrap;
  }

  &__flag--edited {
    background: #e8f6eb;
    color: $green;
  }

  &__flag--kind {
    background: #eaf1fd;
    color: #2f6fdb;
  }

  &__items-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 14px 0 8px;
    color: #374151;
    font-size: 13px;
    font-weight: 600;

    small {
      color: #9ca3af;
      font-weight: 400;
    }

    .el-button {
      color: $green;
      border-color: $green;
    }
  }

  &__items {
    max-height: 180px;
    overflow: auto;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    margin-bottom: 4px;
    border-radius: 8px;
    background: #f7f8f7;

    &.is-ghost {
      opacity: 0.4;
    }
  }

  &__drag {
    flex-shrink: 0;
    color: #9ca3af;
    cursor: grab;

    &:hover {
      color: $green;
    }
  }

  &__item-index {
    flex-shrink: 0;
    width: 18px;
    color: #6b7280;
    font-size: 12px;
    font-weight: 600;
    text-align: center;
  }

  &__time {
    padding: 14px 16px 16px;
    border: 1.5px solid $green;
    border-radius: 10px;
    background: #f1faf3;
  }

  &__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  &__full {
    width: 100%;
  }

  &__limit {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 40px;

    .el-input-number {
      width: 100px;
    }
  }

  &__toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 0;
    color: #111827;
    font-size: 14px;

    & + & {
      border-top: 1px solid #f0f1f0;
    }

    small {
      margin-left: 6px;
      color: #9ca3af;
      font-size: 12px;
    }
  }

  &__toggle:first-of-type {
    margin-top: 12px;
  }

  &__summary {
    padding: 24px;
    border-left: 1px solid #eef0ef;
    background: #f7f8f7;
    overflow: auto;

    h4 {
      margin: 0 0 14px;
      color: #111827;
      font-size: 16px;
    }
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-bottom: 14px;

    div {
      display: flex;
      flex-direction: column;
      padding: 10px 12px;
      border: 1px solid #e8ebe9;
      border-radius: 10px;
      background: #fff;
    }

    strong {
      color: $green;
      font-size: 22px;
    }

    small {
      color: #6b7280;
      font-size: 12px;
    }
  }

  &__group {
    margin-bottom: 10px;
    padding: 10px 12px;
    border: 1px solid #e8ebe9;
    border-radius: 10px;
    background: #fff;
  }

  &__group-head {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    small {
      flex-shrink: 0;
      color: $green;
      font-size: 12px;
    }
  }

  &__group-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  &__facts {
    margin: 16px 0 0;
    padding-top: 12px;
    border-top: 1px solid #e8ebe9;

    div {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      padding: 5px 0;
      font-size: 13px;
    }

    dt {
      color: #6b7280;
    }

    dd {
      margin: 0;
      color: #111827;
      text-align: right;
      word-break: break-word;
    }
  }

  &__muted {
    color: #9ca3af;
    font-size: 12px;
  }

  &__foot {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 28px;
    border-top: 1px solid #eef0ef;

    .el-button--primary {
      min-width: 180px;
      font-weight: 600;
    }
  }

  &__foot-text {
    margin-right: auto;
    color: #6b7280;
    font-size: 13px;
  }
}
</style>
